import { MailIcon, PhoneIcon } from '@heroicons/react/outline'
import { c, FC } from 'lib/component-utils'
import { useUserContext } from 'lib/context'
import { useIntl } from 'lib/intl-utils'
import { ComponentType, useEffect, useState } from 'react'
import { MaxIcon, TelegramIcon, WhatsAppIcon, YouTubeIcon } from 'components/icons'
import { useRouter } from 'next/router'
import Link from 'components/base/Link'
import { useTrackGoal } from 'lib/metrics'
import { useTheme } from 'lib/colors'
import FooterAccreditations from 'components/app/FooterAccreditations'
import { ContactInformation, HostType } from 'configs/theme'
import { getLegalDocuments } from 'configs/legal'

type FooterNavigationGroup = {
  name: string
  links: { name: string; href: string; label?: string }[]
}

const contactLinks = [
  {
    name: 'Telegram',
    prefix: 'https://t.me/',
    href: (c: ContactInformation) => c.telegram,
    icon: TelegramIcon,
  },
  {
    name: 'MAX',
    prefix: '',
    href: (c: ContactInformation) => c.maxSupport,
    icon: MaxIcon,
  },
  {
    name: 'WhatsApp',
    prefix: 'https://wa.me/',
    href: (c: ContactInformation) => c.whatsapp,
    icon: WhatsAppIcon,
  },
  {
    name: 'Email',
    prefix: 'mailto:',
    href: (c: ContactInformation) => c.mail,
    icon: MailIcon,
  },
  {
    name: 'Phone',
    prefix: 'tel:',
    href: (c: ContactInformation) => c.phone,
    icon: PhoneIcon,
  },
]

const socialLinksByHost: Partial<
  Record<
    HostType,
    {
      name: string
      prefix: string
      href: (contactInformation: ContactInformation) => string | undefined
      icon: ComponentType<{ className?: string; 'aria-hidden'?: boolean | 'true' }>
    }[]
  >
> = {
  sellmonitor: [
    {
      name: 'Telegram',
      prefix: 'https://t.me/',
      href: () => 'sellmonitor_com',
      icon: TelegramIcon,
    },
    {
      name: 'MAX',
      prefix: '',
      href: () => 'https://max.ru/id7727492799_biz',
      icon: MaxIcon,
    },
    {
      name: 'YouTube',
      prefix: 'https://youtube.com/',
      href: (c) => c.youtube,
      icon: YouTubeIcon,
    },
  ],
  mayak: [
    {
      name: 'Telegram',
      prefix: 'https://t.me/',
      href: () => 'mayak_marketnews',
      icon: TelegramIcon,
    },
    {
      name: 'MAX',
      prefix: '',
      href: () => 'https://max.ru/join/toZjjtkRjJp1oJXPqOQ99RTzXo4Xz9NWcxSjVuvL6Pg',
      icon: MaxIcon,
    },
    {
      name: 'YouTube',
      prefix: 'https://youtube.com/',
      href: (c) => c.youtube,
      icon: YouTubeIcon,
    },
  ],
}

const navigationByHost: Partial<Record<HostType, FooterNavigationGroup[]>> = {
  sellmonitor: [
    {
      name: 'service',
      links: [
        {
          name: 'extension',
          href: '/extension/',
        },
        { name: 'telegram_bot', href: 'https://t.me/sellmonitor_info_bot' },
        { name: 'installment_plan', href: '/installment-plan/' },
        { name: 'consulting', href: '/consulting/' },
        { name: 'partner_program', href: '/partner-program/' },
        { name: 'mcp_analytics', href: '/marketplace-analytics-mcp/' },
        { name: 'mcp_inner_analytics', href: '/store-analytics-mcp/' },
      ],
    },
    {
      name: 'about_company',
      links: [
        { name: 'about', href: '/about/' },
        { name: 'blog', href: '/blog/' },
        { name: 'contacts', href: '/contacts/' },
        { name: 'media', href: '/media/' },
      ],
    },
  ],
  mayak: [
    {
      name: 'service',
      links: [
        { name: 'extension', href: 'https://mayak.bz/plugin', label: 'Плагин для браузера' },
        { name: 'advertising', href: 'https://mayak.bz/ads', label: 'Управление рекламой' },
        { name: 'competitors', href: 'https://mayak.bz/analyze', label: 'Анализ конкурентов' },
        { name: 'consulting', href: 'https://mayak.bz/consulting', label: 'Маяк.Консалтинг' },
        { name: 'webinars', href: 'https://mayak.bz/webinars_mayak', label: 'Вебинары' },
        { name: 'mcp_analytics', href: '/marketplace-analytics-mcp/' },
        { name: 'mcp_inner_analytics', href: '/store-analytics-mcp/' },
      ],
    },
    {
      name: 'about_company',
      links: [
        { name: 'blog', href: 'https://mayak.bz/journal', label: 'Маяк.Журнал' },
        { name: 'legal', href: 'https://mayak.bz/legal', label: 'Правовая информация' },
        { name: 'help', href: 'https://mayak.bz/faq', label: 'Справочный центр' },
        { name: 'support', href: 'https://t.me/MayakHelp_Bot', label: 'Поддержка' },
        { name: 'partner_program', href: 'https://mayak.bz/partner' },
      ],
    },
  ],
}

const legalLinksByHost: Partial<
  Record<HostType, { name: string; href?: string; label?: string }[]>
> = {
  sellmonitor: [
    { name: 'accreditation', href: '/accreditation/' },
    { name: 'public_offer', href: '/public-offer/' },
    { name: 'privacy_policy', href: '/doc/sellmonitor/ru/privacy_policy.pdf' },
    { name: 'pricing', href: '/pricing-docs/' },
  ],
  mayak: [
    { name: 'legal_information', href: 'https://mayak.bz/legal' },
    { name: 'public_offer' },
    { name: 'privacy_policy' },
  ],
}

const fallbackNavigation: FooterNavigationGroup[] = [
  {
    name: 'service',
    links: [
      {
        name: 'extension',
        href: 'https://chrome.google.com/webstore/detail/sellmonitor/nlldhlipbgolknjpcihjdobjjngeejag',
      },
      { name: 'telegram_bot', href: 'https://t.me/sellmonitor_info_bot' },
      { name: 'installment_plan', href: '/installment-plan/' },
      { name: 'consulting', href: '/consulting/' },
      { name: 'partner_program', href: '/partner-program/' },
      { name: 'mcp_analytics', href: '/marketplace-analytics-mcp/' },
      { name: 'mcp_inner_analytics', href: '/store-analytics-mcp/' },
    ],
  },
  {
    name: 'about_company',
    links: [
      { name: 'about', href: '/about/' },
      { name: 'blog', href: '/blog/' },
      { name: 'contacts', href: '/contacts/' },
    ],
  },
]

const SellmonitorFooter: FC = ({ className }) => {
  const { t, lang } = useIntl()
  const ctx = useUserContext()
  const { theme } = ctx
  const router = useRouter()
  const trackGoal = useTrackGoal()
  const [hide, setHide] = useState(false)
  const { isDark } = useTheme()
  const legalDocuments = getLegalDocuments(theme.hostType)
  const navigation = navigationByHost[theme.hostType] ?? fallbackNavigation
  const socialLinks = socialLinksByHost[theme.hostType] ?? []
  const legalLinks =
    legalLinksByHost[theme.hostType]?.map((link) => ({
      ...link,
      href:
        link.href ??
        (link.name === 'public_offer' ? legalDocuments.offer : legalDocuments.privacyPolicy),
    })) ?? []

  const helpLinks =
    ctx.isAuthorized && theme.hostType !== 'mayak'
      ? [
          { name: 'instructions', href: '/help/inner/sales/' },
          { name: 'changelog', href: '/help/changelog/' },
          ...(ctx.user.hasPreviousPayments || ctx.user.roles.includes('ROLE_ADMIN')
            ? [{ name: 'suggestions.title', href: '/help/suggestions/' }]
            : []),
        ]
      : []

  useEffect(() => {
    const iframe = document.querySelector('main > div > iframe')

    if (iframe !== null) {
      setHide(true)
    } else {
      setHide(false)
    }
  }, [router.asPath])

  const currentYear = new Date().getFullYear()
  const lifespan =
    theme.releaseDate !== currentYear ? theme.releaseDate + '–' + currentYear : theme.releaseDate
  const copyrightTitle = theme.hostType === 'sellmonitor' ? 'Sellematics' : theme.title

  if (hide) return null

  return (
    <footer className="relative overflow-hidden bg-white border-t border-gray-200">
      {theme.hostType === 'sellmonitor' && (
        <div className="absolute w-fit bottom-6 -left-[219px] hidden xl:flex">
          <img
            src={
              isDark ? '/next/img/footer/sellmonitor_dark.svg' : '/next/img/footer/sellmonitor.svg'
            }
            alt="Sellmonitor"
            className="no-dark-filter"
          />
          <img
            src={
              isDark ? '/next/img/footer/sellmonitor_dark.svg' : '/next/img/footer/sellmonitor.svg'
            }
            alt="Sellmonitor"
            className="no-dark-filter"
          />
          <img
            src={
              isDark ? '/next/img/footer/sellmonitor_dark.svg' : '/next/img/footer/sellmonitor.svg'
            }
            alt="Sellmonitor"
            className="no-dark-filter"
          />
          <img
            src={
              isDark ? '/next/img/footer/sellmonitor_dark.svg' : '/next/img/footer/sellmonitor.svg'
            }
            alt="Sellmonitor"
            className="no-dark-filter"
          />
          <img
            src={
              isDark ? '/next/img/footer/sellmonitor_dark.svg' : '/next/img/footer/sellmonitor.svg'
            }
            alt="Sellmonitor"
            className="no-dark-filter"
          />
        </div>
      )}
      <div
        className={c`px-4 pt-12 mx-auto max-w-7xl sm:px-6 lg:pt-16 lg:px-8 ${theme.hostType === 'mayak'} pb-20 | pb-12 lg:pb-28`}
      >
        <div className="mb-12 xl:grid xl:grid-cols-4 xl:gap-8">
          <div className="min-w-0 space-y-4 xl:col-span-2">
            <div>
              <img
                src={isDark ? theme.logoWhite : theme.logoDark}
                className={c`w-32`}
                alt={theme.title}
              />
              {theme.hostType !== 'mayak' && (
                <div className="mt-1 text-xs text-gray-700 [font-weight:480]">
                  powered by Sellematics
                </div>
              )}
            </div>
            <FooterAccreditations />
            <div className="flex items-center space-x-8 sm:space-x-12 !mt-8">
              <div className="space-y-2">
                <div className="text-[13px] font-medium text-gray-700">{t`contact_us`}</div>
                <div className="flex space-x-4">
                  {contactLinks.map((item) => {
                    const onClick =
                      item.name === 'YouTube'
                        ? () => trackGoal('moved-to-youtube')
                        : item.name === 'Telegram' || item.name === 'MAX'
                          ? () => trackGoal('moved-to-messenger')
                          : () => {}
                    const link = item.href(theme.contactInformation)
                    if (!link || (item.name === 'Phone' && theme.hostType === 'sellmonitor')) {
                      return null
                    }
                    return (
                      <a
                        key={item.name}
                        href={item.prefix + link}
                        target="_blank"
                        rel="noreferrer nofollow"
                        onClick={onClick}
                        className="flex items-center justify-center px-2 py-1 rounded-md aspect-1 group text-primary-600 bg-primary-50 hover:bg-primary-600 hover:text-primary-50"
                      >
                        <span className="sr-only">{item.name}</span>
                        <item.icon
                          className={c`w-6 h-6 ${item.name === 'MAX'} scale-[0.9]`}
                          aria-hidden="true"
                        />
                      </a>
                    )
                  })}
                </div>
              </div>
              <div className="space-y-2">
                <div className="text-[13px] font-medium text-gray-700">{t`follow_news`}</div>
                <div className="flex space-x-4">
                  {socialLinks.map((item) => {
                    const onClick =
                      item.name === 'YouTube'
                        ? () => trackGoal('moved-to-youtube')
                        : item.name === 'Telegram' || item.name === 'MAX'
                          ? () => trackGoal('moved-to-messenger')
                          : () => {}
                    const link = item.href(theme.contactInformation)
                    if (!link) {
                      return null
                    }
                    return (
                      <a
                        key={item.name}
                        href={item.prefix + link}
                        target="_blank"
                        rel="noreferrer nofollow"
                        onClick={onClick}
                        className="flex items-center justify-center px-2 py-1 rounded-md text-cyan-600 bg-cyan-50 aspect-1 group hover:bg-cyan-600 hover:text-cyan-50"
                      >
                        <span className="sr-only">{item.name}</span>
                        <item.icon
                          className={c`w-6 h-6 ${item.name === 'MAX'} scale-[0.9]`}
                          aria-hidden="true"
                        />
                      </a>
                    )
                  })}
                </div>
              </div>
            </div>
          </div>
          <div
            className={c`grid min-w-0 grid-cols-1 gap-8 mt-12 break-words xl:col-span-2 xl:mt-0 ${helpLinks.length > 0} sm:grid-cols-3 | sm:grid-cols-2 ${theme.hostType === 'sellmonitor' && helpLinks.length > 0} xl:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,1fr)] ${theme.hostType === 'sellmonitor' && helpLinks.length === 0} xl:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]`}
          >
            {navigation.map(({ name: categoryName, links: categoryLinks }) => (
              <div key={categoryName} className="min-w-0">
                <p className="text-base font-medium text-gray-900">{t(`links.${categoryName}`)}</p>
                <ul role="list" className="mt-4 space-y-4">
                  {categoryLinks.map(({ name: linkName, href: linkHref, label }) => {
                    const onClick =
                      linkName === 'telegram_bot'
                        ? () => trackGoal('moved-to-telegram-bot')
                        : () => {}
                    const isInternal = linkHref.startsWith('/') && !['mcp_analytics', 'mcp_inner_analytics'].includes(linkName)
                    const LinkComponent = isInternal ? Link : 'a'
                    const linkProps = isInternal
                      ? {}
                      : { target: '_blank', rel: 'noreferrer nofollow' }

                    return (
                      <li key={linkName}>
                        <LinkComponent
                          {...linkProps}
                          href={linkHref}
                          onClick={onClick}
                          className="text-base text-gray-500 hover:text-gray-900"
                        >
                          {label ?? t(`links.${linkName}`)}
                        </LinkComponent>
                      </li>
                    )
                  })}
                </ul>
              </div>
            ))}
            {helpLinks.length > 0 && (
              <div className="min-w-0">
                <p className="text-base font-medium text-gray-900">{t`help`}</p>
                <ul role="list" className="mt-4 space-y-4">
                  {helpLinks.map(({ name: linkName, href: linkHref }) => (
                    <li key={linkName}>
                      <Link href={linkHref} className="text-base text-gray-500 hover:text-gray-900">
                        {t(linkName)}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-col items-baseline gap-4 pt-8 border-t border-gray-200 xl:flex-row xl:flex-wrap">
          <div>
            <p className="text-base text-gray-400">
              {lang === 'en' && (
                <>
                  &copy; {lifespan} {copyrightTitle}.
                </>
              )}
              {lang === 'ru' && (
                <>
                  &copy; {copyrightTitle}, {lifespan}.
                </>
              )}
              {lang === 'uz' && (
                <>
                  &copy; {lifespan} {copyrightTitle}.
                </>
              )}
              {lang === 'zh' && (
                <>
                  &copy; {lifespan} {copyrightTitle}.
                </>
              )}{' '}
              {t`footer.all_rights_reserved`}.
            </p>
          </div>
          <div className="flex min-w-0 flex-col gap-y-2 text-gray-400 xl:flex-row xl:flex-wrap xl:gap-x-4 2xl:gap-x-6 xl:ml-auto">
            {legalLinks.map(({ name, href, label }) => {
              if (href === undefined) return null

              return (
                <a key={name} className="hover:text-gray-500" href={href}>
                  {label ?? (name === 'pricing' ? t`links.pricing` : t(`footer.${name}`))}
                </a>
              )
            })}
          </div>
        </div>
      </div>
    </footer>
  )
}

export default SellmonitorFooter
